import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, Package, Check } from 'lucide-react';
import { useAlert } from '../../../context/AlertContext';
import { useConfirm } from '../../../context/ConfirmContext';
import Layout from '../../../components/Layout';
import API_BASE_URL from '../../../config/api';

const PackagesManagement = () => {
    const [packages, setPackages] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);

    // Package Form State
    const [formData, setFormData] = useState({
        package_name: '',
        package_code: '',
        description: '',
        price: '',
        pv_points: '',
        direct_bonus: '',
        matching_bonus_percentage: '',
        performance_bonus: '',
        image_url: '',
        status: 'active',
        products: [] // Array of { product_id, quantity }
    });

    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const [packagesRes, productsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/admin/packages_management.php`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/admin/products.php`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const packagesData = await packagesRes.json();
            const productsData = await productsRes.json();

            if (packagesData.success) setPackages(packagesData.packages);
            if (productsData.success) setProducts(productsData.products);

        } catch (error) {
            console.error('Error fetching data:', error);
            showAlert('error', 'Failed to fetch packages data');
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Product Selection Handling
    const handleProductToggle = (productId) => {
        setFormData(prev => {
            const exists = prev.products.find(p => p.product_id === productId);
            if (exists) {
                return {
                    ...prev,
                    products: prev.products.filter(p => p.product_id !== productId)
                };
            } else {
                return {
                    ...prev,
                    products: [...prev.products, { product_id: productId, quantity: 1 }]
                };
            }
        });
    };

    const handleProductQuantityChange = (productId, quantity) => {
        if (quantity < 1) return;
        setFormData(prev => ({
            ...prev,
            products: prev.products.map(p =>
                p.product_id === productId ? { ...p, quantity: parseInt(quantity) } : p
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.products.length === 0) {
            showAlert('warning', 'Please select at least one product for the package');
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const url = `${API_BASE_URL}/admin/packages_management.php`;
            const method = editingPackage ? 'PUT' : 'POST';
            const body = editingPackage ? { ...formData, id: editingPackage.id } : formData;

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.success) {
                showAlert('success', `Package ${editingPackage ? 'updated' : 'created'} successfully`);
                fetchData();
                closeModal();
            } else {
                showAlert('error', data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving package:', error);
            showAlert('error', 'An error occurred while saving');
        }
    };

    const handleDelete = (pkg) => {
        showConfirm(
            'Delete Package',
            `Are you sure you want to delete "${pkg.package_name}"? This will hide it from the shop but keep it for existing users.`,
            'danger'
        ).then(async (confirmed) => {
            if (confirmed) {
                try {
                    const token = localStorage.getItem('authToken');
                    const response = await fetch(`${API_BASE_URL}/admin/packages_management.php`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ id: pkg.id })
                    });
                    const data = await response.json();
                    if (data.success) {
                        showAlert('success', 'Package deleted successfully');
                        fetchData();
                    } else {
                        showAlert('error', data.message || 'Delete failed');
                    }
                } catch (error) {
                    console.error('Delete error:', error);
                    showAlert('error', 'An error occurred while deleting');
                }
            }
        });
    };

    const openModal = async (pkg = null) => {
        if (pkg) {
            // Fetch full package details including products
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/admin/packages_management.php?id=${pkg.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();

                if (data.success) {
                    const fullPkg = data.package;
                    setEditingPackage(fullPkg);
                    setFormData({
                        package_name: fullPkg.package_name,
                        package_code: fullPkg.package_code,
                        description: fullPkg.description || '',
                        price: fullPkg.price,
                        pv_points: fullPkg.pv_points,
                        direct_bonus: fullPkg.direct_bonus,
                        matching_bonus_percentage: fullPkg.matching_bonus_percentage,
                        performance_bonus: fullPkg.performance_bonus,
                        image_url: fullPkg.image_url || '',
                        status: fullPkg.status,
                        products: fullPkg.products.map(p => ({
                            product_id: p.product_id,
                            quantity: p.quantity
                        }))
                    });
                    setShowModal(true);
                }
            } catch (error) {
                showAlert('error', 'Failed to load package details');
            }
        } else {
            setEditingPackage(null);
            setFormData({
                package_name: '',
                package_code: '',
                description: '',
                price: '',
                pv_points: '',
                direct_bonus: '',
                matching_bonus_percentage: '',
                performance_bonus: '',
                image_url: '',
                status: 'active',
                products: []
            });
            setShowModal(true);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingPackage(null);
    };

    return (
        <Layout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Packages Management</h1>
                        <p className="text-gray-600">Create and manage membership packages</p>
                    </div>
                    <button
                        onClick={() => openModal()}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus size={20} />
                        Create Package
                    </button>
                </div>

                {/* Packages Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {loading ? (
                        <div className="col-span-full py-12 text-center text-gray-500">Loading packages...</div>
                    ) : packages.length === 0 ? (
                        <div className="col-span-full py-12 text-center text-gray-500">No packages found</div>
                    ) : (
                        packages.map((pkg) => (
                            <div key={pkg.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                                <div className={`h-2 ${pkg.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <div className="p-5">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="font-bold text-lg text-gray-900">{pkg.package_name}</h3>
                                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{pkg.package_code}</span>
                                        </div>
                                        <Package className="text-blue-500" size={24} />
                                    </div>

                                    <div className="space-y-2 mb-4">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Price</span>
                                            <span className="font-semibold text-gray-900"> {parseFloat(pkg.price).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">PV Points</span>
                                            <span className="font-semibold text-gray-900">{parseFloat(pkg.pv_points).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Products</span>
                                            <span className="font-semibold text-blue-600">{pkg.product_count || 0} items</span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2 mt-4 pt-4 border-t border-gray-50">
                                        <button
                                            onClick={() => openModal(pkg)}
                                            className="flex-1 flex items-center justify-center gap-2 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium"
                                        >
                                            <Edit2 size={16} /> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(pkg)}
                                            className="flex-1 flex items-center justify-center gap-2 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors text-sm font-medium"
                                        >
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 sticky top-0 z-10">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingPackage ? 'Edit Package' : 'Create New Package'}
                                </h2>
                                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-8">
                                {/* Basic Info */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <Check size={20} className="text-green-500" /> Basic Information
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Package Name</label>
                                            <input
                                                type="text"
                                                name="package_name"
                                                value={formData.package_name}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Package Code</label>
                                            <input
                                                type="text"
                                                name="package_code"
                                                value={formData.package_code}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none uppercase"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Price ( )</label>
                                            <input
                                                type="number"
                                                name="price"
                                                value={formData.price}
                                                onChange={handleInputChange}
                                                required
                                                min="0"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">PV Points</label>
                                            <input
                                                type="number"
                                                name="pv_points"
                                                value={formData.pv_points}
                                                onChange={handleInputChange}
                                                min="0"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                rows="3"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>

                                {/* Income Settings */}
                                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <Check size={20} className="text-green-500" /> Income Settings
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Direct Bonus ( )</label>
                                            <input
                                                type="number"
                                                name="direct_bonus"
                                                value={formData.direct_bonus}
                                                onChange={handleInputChange}
                                                min="0"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Matching Bonus (%)</label>
                                            <input
                                                type="number"
                                                name="matching_bonus_percentage"
                                                value={formData.matching_bonus_percentage}
                                                onChange={handleInputChange}
                                                min="0"
                                                max="100"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Performance Bonus ( )</label>
                                            <input
                                                type="number"
                                                name="performance_bonus"
                                                value={formData.performance_bonus}
                                                onChange={handleInputChange}
                                                min="0"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Product Selection */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <Check size={20} className="text-green-500" /> Included Products
                                    </h3>

                                    <div className="border border-gray-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-3 w-10">Select</th>
                                                    <th className="px-4 py-3">Product Name</th>
                                                    <th className="px-4 py-3">Price</th>
                                                    <th className="px-4 py-3 w-32">Quantity</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {products.map(product => {
                                                    const isSelected = formData.products.find(p => p.product_id === product.id);
                                                    return (
                                                        <tr key={product.id} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                                                            <td className="px-4 py-3 text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!isSelected}
                                                                    onChange={() => handleProductToggle(product.id)}
                                                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="font-medium text-gray-900">{product.product_name}</div>
                                                                <div className="text-xs text-gray-500">{product.category_name}</div>
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-600">
                                                                {parseFloat(product.price).toLocaleString()}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {isSelected ? (
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        value={isSelected.quantity}
                                                                        onChange={(e) => handleProductQuantityChange(product.id, e.target.value)}
                                                                        className="w-20 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 outline-none"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-400 text-sm">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="pt-6 flex justify-end gap-3 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                                    >
                                        <Save size={18} />
                                        Save Package
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default PackagesManagement;


